import React from "react";
import { Check, Crown } from "lucide-react";
import { PACKAGES } from "@/constants/testIds";
import { Button } from "@/components/ui/button";

const packages = [
  {
    slug: "standard",
    name: "Standard",
    subtitle: "The essentials",
    description:
      "Full interior and exterior detail with spray-on wax finish.",
    image:
      "https://images.unsplash.com/photo-1520340356584-f9917d1eea6f?w=1200&q=85&fit=crop",
    tiers: [
      { label: "2-Door", price: "$175" },
      { label: "4-Door", price: "$225" },
      { label: "SUV / Truck", price: "$275" },
    ],
    features: ["Full interior vacuum", "Exterior wash", "Spray-on wax", "Tire dressing"],
    accent: false,
  },
  {
    slug: "executive",
    name: "Executive",
    subtitle: "Deep decontamination",
    description:
      "Clay bar, water spot removal, iron remover, compound/polish + wax.",
    image:
      "https://images.unsplash.com/photo-1616422285623-13ff0162193c?w=1200&q=85&fit=crop",
    tiers: [
      { label: "2-Door", price: "$375" },
      { label: "4-Door", price: "$475" },
      { label: "SUV / Truck", price: "$575" },
    ],
    features: [
      "Everything in Standard",
      "Clay bar treatment",
      "Water spot removal",
      "Compound & polish",
    ],
    accent: false,
  },
  {
    slug: "elite",
    name: "Elite",
    subtitle: "Ceramic protection",
    description:
      "Full detail with 1 layer of ceramic coating for lasting shine.",
    image:
      "https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=800&q=85&fit=crop",
    tiers: [
      { label: "2-Door", price: "$699" },
      { label: "4-Door", price: "$799" },
      { label: "SUV / Truck", price: "$899" },
    ],
    features: [
      "Everything in Executive",
      "Iron remover",
      "1 layer ceramic coating",
      "Paint correction",
    ],
    accent: true,
  },
  {
    slug: "presidential",
    name: "Presidential",
    subtitle: "The apex package",
    description:
      "3 ceramic layers on paint, plus coated wheels and glass.",
    image:
      "https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?w=1200&q=85&fit=crop",
    tiers: [{ label: "2-Door", price: "$1,199" }],
    features: [
      "Everything in Elite",
      "3 layers ceramic on paint",
      "Ceramic coating on wheels",
      "Ceramic coating on glass",
    ],
    accent: false,
    crown: true,
  },
];

export default function PricingPackages() {
  const scrollToContact = (e) => {
    e.preventDefault();
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="packages"
      data-testid={PACKAGES.root}
      className="relative py-28 md:py-36 bg-gradient-to-b from-[#050505] via-[#0a0a0a] to-[#050505]"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <div className="mb-16 max-w-3xl">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-10 bg-cyan-400" />
            <span className="text-cyan-400 uppercase tracking-[0.35em] text-xs font-medium">
              Detailing Packages
            </span>
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl font-black uppercase tracking-tighter leading-none text-white">
            Choose your <span className="text-cyan-400">level of shine.</span>
          </h2>
          <p className="mt-6 text-zinc-400 leading-relaxed">
            Four tiered packages, from a spotless standard wash to the
            Presidential 3-layer ceramic package. Prices vary by vehicle size.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {packages.map((p) => (
            <article
              key={p.slug}
              data-testid={PACKAGES.card(p.slug)}
              className={`relative flex flex-col border ${
                p.crown
                  ? "border-cyan-400/60 shadow-[0_0_40px_rgba(0,229,255,0.15)]"
                  : p.accent
                  ? "border-white/20"
                  : "border-white/10"
              } bg-white/[0.02] backdrop-blur-md group hover:border-cyan-400/60 transition-colors overflow-hidden`}
            >
              {/* Package hero image */}
              <div className="relative h-44 overflow-hidden">
                <img
                  src={p.image}
                  alt={`${p.name} package`}
                  className="w-full h-full object-cover grayscale-[15%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-black/40 to-black/20" />
                {p.crown && (
                  <div className="absolute top-3 left-3 bg-cyan-400 text-black px-3 py-1 text-[10px] font-bold uppercase tracking-widest flex items-center gap-1.5 z-10">
                    <Crown className="w-3 h-3" />
                    Signature
                  </div>
                )}
                {p.accent && !p.crown && (
                  <div className="absolute top-3 left-3 border border-white/40 bg-black/60 backdrop-blur-sm text-white px-3 py-1 text-[10px] font-bold uppercase tracking-widest">
                    Most Popular
                  </div>
                )}
              </div>

              <div className="flex flex-col p-8 pt-6 flex-1">
                <div className="uppercase tracking-widest text-xs text-zinc-500">
                  {p.subtitle}
                </div>
                <h3 className="mt-3 font-heading text-3xl font-black uppercase tracking-tighter text-white">
                  {p.name}
                </h3>
                <p className="mt-3 text-sm text-zinc-400 leading-relaxed min-h-[3.5rem]">
                  {p.description}
                </p>

                <div className="mt-6 divide-y divide-white/5 border-y border-white/5">
                  {p.tiers.map((t) => (
                    <div
                      key={t.label}
                      className="flex items-center justify-between py-3"
                    >
                      <span className="text-sm text-zinc-400">{t.label}</span>
                      <span className="font-heading text-xl font-bold text-white">
                        {t.price}
                      </span>
                    </div>
                  ))}
                </div>

                <ul className="mt-6 space-y-2.5 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-zinc-300">
                      <Check className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  data-testid={PACKAGES.cta(p.slug)}
                  onClick={scrollToContact}
                  className={`mt-8 rounded-none font-semibold uppercase tracking-widest text-xs h-11 ${
                    p.crown
                      ? "bg-cyan-400 hover:bg-cyan-300 text-black shadow-[0_0_20px_rgba(0,229,255,0.35)]"
                      : "bg-white/5 hover:bg-cyan-400 hover:text-black border border-white/20 text-white"
                  }`}
                >
                  Book {p.name}
                </Button>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
